import { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../styling/stylingHelper';

interface ProductNameEditModalProps {
  currentName: string;
  onUpdateName: (newName: string) => Promise<void>;
  onClose: () => void;
}

const ProductNameEditModal = ({
  currentName,
  onUpdateName,
  onClose,
}: ProductNameEditModalProps) => {
  const [name, setName] = useState<string>(currentName);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      alert('Please enter a valid product name');
      return;
    }

    if (trimmedName === currentName) {
      alert('The name is the same as the current name');
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdateName(trimmedName);
      onClose();
    } catch (error) {
      console.error('Failed to update name:', error);
      alert('Failed to update product name');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          "bg-white dark:bg-background-dark rounded-xl shadow-xl w-full max-w-md border",
          borderPrimary
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className={cn(semiBoldText, largeTextSize, primaryText, "mb-1")}>
                Edit Product Name
              </h2>
              <p className={cn(smallTextSize, secondaryText)}>
                All occurrences in the selected time period will be updated
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Name Input */}
          <div className="mb-4">
            <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
              Product Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={cn(
                "w-full p-3 border rounded-xl",
                borderPrimary,
                baseTextSize,
                primaryText,
                "focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-500"
              )}
              placeholder="Enter product name"
              autoFocus
            />
            <p className={cn(smallTextSize, secondaryText, "mt-2")}>
              Current: {currentName}
            </p>
          </div>

          {/* Actions */}
          <div className="-mx-4">
            <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
            <div className="flex justify-end gap-3 pt-4 px-4">
              <button
                onClick={onClose}
                className={cn(
                  smallTextSize,
                  secondaryText,
                  semiBoldText,
                  "rounded-xl px-4 py-3",
                  whiteBG,
                  "border",
                  borderPrimary,
                  "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark",
                  "flex-1"
                )}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={cn(
                  smallTextSize,
                  "text-white dark:text-white",
                  semiBoldText,
                  "bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-4 py-3 border",
                  borderPrimary,
                  "hover:bg-darkGreenColor/95",
                  "flex-1",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isSubmitting ? 'Updating...' : 'Update Name'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductNameEditModal;
